/**
 * Record outcome (Customer 360, 01 §7): what happened on a call or a visit, and
 * the next date — saved exactly as before (`record_follow_up`, unchanged).
 *
 * What changed is the amounts a promise offers. They come from the account's
 * own figures by the product's words (`recordAmountChoices`): the overdue, when
 * it is known and above zero, never more than the balance; all of it; or any
 * other amount, typed. An age is never offered under the name "Overdue"; a
 * sheet offers "Past critical age", labelled as such, because a sheet cannot
 * say what is overdue.
 *
 * It writes into the account's activity, so it opens there (owner, 24 Oct
 * 2026): in the rail beside the work from 1280px, the rail giving way to it and
 * coming back with the new entry on top; above the whole history on Activity;
 * over the page where neither is on screen, a sheet on a phone with Record in
 * its own footer. The half-written outcome belongs to the account, not to any
 * of them: going back to the activity, a window made narrower, a tablet turned
 * or the sheet closed and opened again finds it as it was left.
 */
import { useEffect, useRef, useState } from 'react';
import { Button, DialogShell, IconChevronLeft, buttonClass, chipClass, cx, errorClass, fieldClass, helpClass, labelClass } from '@timely/ui';
import {
    OUTCOME_CHOICES, draftProblems, emptyDraft, followUpCall, outcomeChoice, presetDay, recordAmountChoices, suggestedNextFollowUp,
    type FollowUpOutcome,
} from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import { recordFollowUp } from '../../book/useBook';
import { DateField } from '../../forms/DateField';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { useAccount } from './accountPage';

/** The form's working parts: the account's draft, its problems, and saving it. */
function useRecordOutcome(onDone?: () => void) {
    const page = useAccount();
    const { workspace } = useActiveWorkspace();
    const { customer, updatedAt, busy, setBusy } = page;
    const { draft, setDraft, setOther } = page.record;
    const [showProblems, setShowProblems] = useState(false);
    // The company's day, not the browser's: "not in the past" and "the day after" mean the same day here as in the database.
    const { today, date } = useFormatters();
    const problems = draftProblems(draft, today);

    const save = () => {
        if (problems.length > 0) { setShowProblems(true); return; }
        setBusy(true);
        setShowProblems(false);
        void recordFollowUp(followUpCall(workspace.id, customer.id, draft, { expectedUpdatedAt: updatedAt }) as unknown as Record<string, unknown>)
            .then(async () => {
                setDraft(emptyDraft('note'));
                setOther(false);
                onDone?.();
                await page.changed('Recorded.');
            })
            .catch((error: Error) => page.failed(error.message))
            .finally(() => setBusy(false));
    };
    const outcome = draft.clearFollowUp ? 'Adds this to the history. No next follow-up will be set.'
        : draft.nextFollowUp ? `Adds this to the history and sets the next follow-up for ${date.weekday(draft.nextFollowUp)}.`
            : 'Adds this to the history. The next follow-up stays as it is.';
    return {
        busy, save, outcome,
        problemFor: (field: string) => (showProblems ? problems.find(p => p.field === field)?.message : undefined),
        hideProblems: () => setShowProblems(false),
    };
}

/** What happened, what was promised, what was said, and the next date: the form's fields, wherever it sits. */
function RecordOutcomeFields({ idPrefix, form }: { idPrefix: string; form: ReturnType<typeof useRecordOutcome> }) {
    const page = useAccount();
    const { draft, setDraft, other, setOther } = page.record;
    const { today, money } = useFormatters();
    const { busy, problemFor } = form;
    const balance = page.customer.totalType === 'Cr' ? 0 : Math.abs(Number(page.customer.total) || 0);
    const choices = recordAmountChoices(page.overdue, balance);
    const choice = outcomeChoice(draft.kind);
    const id = (name: string) => `${idPrefix}-${name}`;

    const pick = (kind: FollowUpOutcome) => {
        form.hideProblems();
        // The form suggests a next date the moment an outcome is chosen: the day after a promise, tomorrow after no
        // answer, nothing after a payment.
        setDraft(previous => ({ ...previous, kind, nextFollowUp: suggestedNextFollowUp({ kind, promisedOn: previous.promisedOn }, today) }));
    };
    // An amount and its words, the words free to wrap: "Past critical age · over 90 days" is longer than a phone's chip.
    const amountChip = (on: boolean) => cx(chipClass(on), 'flex-col !items-start !h-auto !whitespace-normal py-1.5 text-left');

    // Laid out by the room the form has, not the screen's: the same fields fill a 340px rail, a dialog and a phone.
    return (
        <div className="@container">
            <fieldset disabled={busy}>
                <legend className={labelClass}>What happened?</legend>
                {/* Equal cells, as many across as fit with the longest label whole (2026-10 global sweep). */}
                <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-1.5">
                    {OUTCOME_CHOICES.map(option => (
                        <button key={option.kind} type="button" aria-pressed={draft.kind === option.kind} title={option.hint}
                            onClick={() => pick(option.kind)} className={cx(chipClass(draft.kind === option.kind), 'justify-center')}>
                            {option.label}
                        </button>
                    ))}
                </div>
                {choice?.hint && <p className={helpClass}>{choice.hint}</p>}
            </fieldset>

            {choice?.needsPromise && (
                <fieldset disabled={busy} className="mt-4 grid gap-4 @min-[30rem]:grid-cols-2 rounded-control bg-card-2 p-4">
                    <div>
                        <p id={id('how-much')} className={labelClass}>How much</p>
                        <div role="group" aria-labelledby={id('how-much')} className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-1.5">
                            {choices.map(c => {
                                const value = String(c.amount);
                                const on = !other && draft.promisedAmount === value;
                                return (
                                    <button key={c.key} type="button" aria-pressed={on} onClick={() => { setOther(false); setDraft(d => ({ ...d, promisedAmount: value })); }}
                                        className={amountChip(on)}>
                                        <span className="text-caption font-semibold">{c.label}</span>{' '}
                                        <span className="num text-secondary">{money.exact(c.amount)}</span>
                                    </button>
                                );
                            })}
                            <button type="button" aria-pressed={other}
                                onClick={() => { setOther(true); setDraft(d => ({ ...d, promisedAmount: '' })); }}
                                className={amountChip(other)}>
                                <span className="text-caption font-semibold">Other</span>{' '}
                                <span className="num text-secondary">An amount</span>
                            </button>
                        </div>
                        {(other || choices.length === 0) && (
                            <div className="mt-2">
                                <label htmlFor={id('amount')} className="sr-only">Amount promised</label>
                                <input id={id('amount')} type="number" inputMode="decimal" value={draft.promisedAmount} autoFocus={other}
                                    aria-invalid={problemFor('promisedAmount') ? true : undefined}
                                    onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, promisedAmount: v })); }}
                                    className={fieldClass({ invalid: Boolean(problemFor('promisedAmount')), className: 'w-full num' })} />
                            </div>
                        )}
                        <p className={helpClass}>What they said they would pay — expected, not received.</p>
                        {problemFor('promisedAmount') && <p className={errorClass} role="alert">{problemFor('promisedAmount')}</p>}
                    </div>
                    <DateField
                        id={id('promise-date')} label="By when" presets={[0, 1, 3, 7]} notBefore={presetDay(0, today)}
                        value={draft.promisedOn} error={problemFor('promisedOn')}
                        onChange={value => setDraft(d => ({ ...d, promisedOn: value, nextFollowUp: suggestedNextFollowUp({ kind: d.kind, promisedOn: value }, today) }))}
                    />
                </fieldset>
            )}

            <fieldset disabled={busy} className="mt-4 space-y-4">
                <div>
                    <label htmlFor={id('body')} className={labelClass}>What was said</label>
                    <textarea id={id('body')} rows={2} value={draft.body} aria-invalid={problemFor('body') ? true : undefined}
                        onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, body: v })); }}
                        className={fieldClass({ multiline: true, invalid: Boolean(problemFor('body')), className: 'w-full resize-y' })} />
                    {problemFor('body') && <p className={errorClass} role="alert">{problemFor('body')}</p>}
                </div>
                <div className="grid gap-4 @min-[30rem]:grid-cols-2 items-start">
                    <DateField
                        id={id('next')} label="Next follow-up" presets={[1, 3, 7]} notBefore={presetDay(0, today)}
                        value={draft.nextFollowUp} error={problemFor('nextFollowUp')} disabled={draft.clearFollowUp}
                        onChange={value => setDraft(d => ({ ...d, nextFollowUp: value }))}
                    />
                    <div className="flex flex-col gap-1 @min-[30rem]:pt-6">
                        <label className="flex items-center gap-2.5 text-body text-label min-h-9 cursor-pointer">
                            <input type="checkbox" checked={draft.clearFollowUp}
                                onChange={e => { const on = e.target.checked; setDraft(d => ({ ...d, clearFollowUp: on, nextFollowUp: on ? '' : d.nextFollowUp })); }} />
                            No further follow-up needed
                        </label>
                        <label className="flex items-center gap-2.5 text-body text-label min-h-9 cursor-pointer">
                            <input type="checkbox" checked={draft.completed} onChange={e => { const on = e.target.checked; setDraft(d => ({ ...d, completed: on })); }} />
                            The money has been collected
                        </label>
                    </div>
                </div>
            </fieldset>
        </div>
    );
}

/** The toolbar's Record outcome that is on screen: focus goes back to it when the composer closes. */
const backToRecordButton = () => {
    const shown = [...document.querySelectorAll<HTMLElement>('[data-record-outcome]')].find(b => b.offsetParent !== null);
    shown?.focus({ preventScroll: true });
};

/**
 * Record outcome in the account's activity (the rail from 1280px, or above the whole history): the activity gives
 * way to it, and comes back when it is recorded — the new entry on top — or when somebody goes back to it. Going back
 * keeps what was written; the activity says so and offers to carry on.
 */
export function RecordOutcomeComposer({ idPrefix = 'record-rail' }: {
    /** Ids inside the form, unique on the page (the dialog's form may exist too). */
    idPrefix?: string;
}) {
    const page = useAccount();
    const close = () => { page.record.compose(false); requestAnimationFrame(backToRecordButton); };
    const form = useRecordOutcome(close);
    const body = useRef<HTMLFormElement>(null);
    // The outcome chosen is where somebody starts: focus on it, as a dialog would.
    useEffect(() => { body.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus({ preventScroll: true }); }, []);
    return (
        <section aria-labelledby={`${idPrefix}-title`} className="rounded-card border border-separator bg-card shadow-e0 min-w-0">
            <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3 border-b border-separator min-h-14">
                <h3 id={`${idPrefix}-title`} className="font-display stretch-title text-heading font-semibold text-label tracking-heading">Record outcome</h3>
                <button type="button" onClick={close} disabled={form.busy} className={buttonClass({ variant: 'quiet', size: 'sm', className: '-mr-2' })}>
                    <IconChevronLeft size={14} />Back to activity
                </button>
            </div>
            <form ref={body} noValidate onSubmit={e => { e.preventDefault(); form.save(); }} className="px-5 pt-4 pb-5">
                <RecordOutcomeFields idPrefix={idPrefix} form={form} />
                <div className="mt-5 pt-4 border-t border-separator flex items-center gap-x-3 gap-y-2 flex-wrap">
                    <Button type="submit" variant="primary" disabled={form.busy} busy={form.busy}>{form.busy ? 'Saving…' : 'Record'}</Button>
                    <p className="text-caption text-label-3 min-w-0 flex-1 basis-48">{form.outcome}</p>
                </div>
            </form>
        </section>
    );
}

/**
 * Record outcome over the page: a dialog on a wide screen, a sheet from the bottom on a phone. Record and what it
 * will do sit in the sheet's footer, always on screen and clear of a phone's home indicator, however long the form.
 */
export function RecordOutcomeDialog({ onClose }: { onClose: () => void }) {
    const page = useAccount();
    const form = useRecordOutcome(onClose);
    return (
        <DialogShell
            title="Record outcome" subtitle={page.customer.company} size="md" onClose={onClose} closeOnEscape={!page.busy}
            onSubmit={e => { e.preventDefault(); form.save(); }}
            footerNote={form.outcome}
            footer={<Button type="submit" variant="primary" disabled={form.busy} busy={form.busy}>{form.busy ? 'Saving…' : 'Record'}</Button>}
        >
            <RecordOutcomeFields idPrefix="record-dialog" form={form} />
        </DialogShell>
    );
}
